import React, { useEffect, useState } from 'react';
import { Loader2, Radio, CalendarPlus, PlayCircle, ExternalLink, Video, ListVideo } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import LiveChat from '../components/LiveChat';
import { Link } from 'react-router-dom';
import api, { mediaUrl } from '../lib/api';
import { toEmbedUrl, isJoinLink, googleCalendarUrl, toPlaylistEmbed } from '../lib/embed';

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return iso; }
};

const Player = ({ s, url }) => {
  if (isJoinLink(s.platform, url)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="pill-btn" data-testid={`live-join-${s.id}`}>
        <ExternalLink className="w-4 h-4" /> Join the session
      </a>
    );
  }
  const src = toEmbedUrl(url, s.platform);
  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
      <iframe src={src} title={s.title} className="absolute inset-0 w-full h-full" frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen data-testid={`live-player-${s.id}`} />
    </div>
  );
};

const LiveStream = () => {
  const [items, setItems] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setInterval(() => api.get('/live').then((r) => setItems(r.data || [])).catch(() => {}), 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/live').then((r) => setItems(r.data || [])).catch(() => setItems([])),
      api.get('/live/playlists').then((r) => setPlaylists(r.data || [])).catch(() => setPlaylists([])),
    ]).finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const live = items.filter((s) => s.is_live && s.stream_url);
  const upcoming = items.filter((s) => !s.is_live && s.scheduled_at && new Date(s.scheduled_at).getTime() > now)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const past = items.filter((s) => !s.is_live && s.recording_url && (!s.scheduled_at || new Date(s.scheduled_at).getTime() <= now))
    .sort((a, b) => new Date(b.scheduled_at || 0) - new Date(a.scheduled_at || 0));

  return (
    <div className="App" data-testid="page-live">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3 inline-flex items-center gap-2 justify-center"><Radio className="w-4 h-4 text-rose-italic" /> Live Satsang &amp; Meditations</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-burgundy leading-tight">Live <span className="font-italic-display text-rose-italic">Streaming</span></h1>
            <p className="mt-5 text-mahogany/80 max-w-2xl mx-auto">Join our meditations, discourses and celebrations as they happen — and revisit past sessions anytime.</p>
            <Link to="/members/live" className="pill-btn-outline mt-5 inline-flex" data-testid="live-members-room-link">Members&rsquo; Live Room</Link>
          </div>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : (
            <div className="mt-12 space-y-16">
              {/* LIVE NOW */}
              {live.length > 0 && (
                <div data-testid="live-now-section">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-red-600" /></span>
                    <h2 className="font-serif-display text-2xl text-burgundy">Live Now</h2>
                  </div>
                  <div className="space-y-8">
                    {live.map((s) => (
                      <div key={s.id} className="bg-white/70 rounded-3xl border border-rose/40 p-4 grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5" data-testid={`live-now-${s.id}`}>
                        <div className="min-w-0">
                          <Player s={s} url={s.stream_url} />
                          <h3 className="font-serif-display text-xl text-burgundy mt-4">{s.title}</h3>
                          {s.host_name && <div className="text-sm text-mahogany/70">with {s.host_name}</div>}
                          {s.description && <p className="text-sm text-burgundy/80 mt-2">{s.description}</p>}
                        </div>
                        <LiveChat sessionId={s.id} hostUserId={s.owner_user_id} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* UPCOMING */}
              <div data-testid="live-upcoming-section">
                <h2 className="font-serif-display text-2xl text-burgundy mb-5">Upcoming Sessions</h2>
                {upcoming.length === 0 ? (
                  <p className="text-mahogany/60 text-sm">No sessions scheduled yet — check back soon. 🙏</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {upcoming.map((s) => (
                      <div key={s.id} className="bg-white/70 rounded-3xl border border-rose/40 overflow-hidden" data-testid={`upcoming-${s.id}`}>
                        {s.thumbnail_url ? <SmartImage src={mediaUrl(s.thumbnail_url)} alt={s.title} className="aspect-video" /> : <div className="aspect-video bg-rose-soft/50 flex items-center justify-center"><Video className="w-8 h-8 text-mahogany/40" /></div>}
                        <div className="p-5">
                          <div className="text-xs uppercase tracking-wider text-rose-italic mb-1">{fmtDate(s.scheduled_at)}</div>
                          <h3 className="font-serif-display text-lg text-burgundy leading-tight">{s.title}</h3>
                          {s.host_name && <div className="text-xs text-mahogany/70 mt-1">with {s.host_name}</div>}
                          {s.description && <p className="text-sm text-burgundy/80 mt-2 line-clamp-2">{s.description}</p>}
                          {googleCalendarUrl(s) && (
                            <a href={googleCalendarUrl(s)} target="_blank" rel="noreferrer" className="pill-btn-outline mt-3 text-xs py-1.5" data-testid={`remind-${s.id}`}>
                              <CalendarPlus className="w-3.5 h-3.5" /> Add to Calendar
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* PAST RECORDINGS */}
              {past.length > 0 && (
                <div data-testid="live-past-section">
                  <h2 className="font-serif-display text-2xl text-burgundy mb-5">Past Recordings</h2>
                  <div className="grid lg:grid-cols-2 gap-8">
                    {past.map((s) => (
                      <div key={s.id} className="bg-white/70 rounded-3xl border border-rose/40 p-4" data-testid={`past-${s.id}`}>
                        <Player s={s} url={s.recording_url} />
                        <h3 className="font-serif-display text-xl text-burgundy mt-4 flex items-center gap-2"><PlayCircle className="w-5 h-5 text-rose-italic" /> {s.title}</h3>
                        {s.host_name && <div className="text-sm text-mahogany/70">with {s.host_name}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PLAYLISTS */}
              {playlists.length > 0 && (
                <div data-testid="live-playlists-section">
                  <div className="flex items-center gap-2 mb-5">
                    <ListVideo className="w-5 h-5 text-rose-italic" />
                    <h2 className="font-serif-display text-2xl text-burgundy">Watch &amp; Learn — Playlists</h2>
                  </div>
                  <div className="grid lg:grid-cols-2 gap-8">
                    {playlists.map((p) => (
                      <div key={p.id} className="bg-white/70 rounded-3xl border border-rose/40 p-4" data-testid={`playlist-${p.id}`}>
                        <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
                          <iframe src={toPlaylistEmbed(p.url)} title={p.title} className="absolute inset-0 w-full h-full" frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen data-testid={`playlist-player-${p.id}`} />
                        </div>
                        <h3 className="font-serif-display text-xl text-burgundy mt-4">{p.title}</h3>
                        {p.description && <p className="text-sm text-burgundy/80 mt-1">{p.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LiveStream;
